import { useRef, useState } from 'react';
import { Badge, Dialog, Field } from './shared';
import { KeyframeImage } from './KeyframeReview';
import type { Asset } from './production';
import { SpeechLines, type SpeechLine } from './Speech';

export type MediaPreview = {
  stepId: string;
  shotId: string;
  ordinal: number;
  kind: 'video' | 'image' | 'voice';
  state: string;
  asset?: Asset;
  poster?: Asset;
  errorMessage?: string;
  taskId?: string;
  speechLines?: SpeechLine[];
  warnings?: string[];
  timingValid?: boolean;
  speechPartial?: boolean;
};

export function MediaPlayer({
  asset,
  label,
  poster,
}: {
  asset: Asset;
  label: string;
  poster?: Asset | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const inlineVideo = useRef<HTMLVideoElement>(null);
  if (asset.mimeType.startsWith('image/')) return <KeyframeImage asset={asset} label={label} />;
  if (asset.mimeType.startsWith('audio/'))
    return <audio controls preload="metadata" src={asset.url} aria-label={label} />;
  return (
    <>
      <video
        ref={inlineVideo}
        className="media-shot-video"
        controls
        playsInline
        preload="metadata"
        src={asset.url}
        poster={poster?.url}
        aria-label={label}
      />
      <button
        className="button secondary"
        onClick={() => {
          inlineVideo.current?.pause();
          setExpanded(true);
        }}
        aria-label={`放大播放${label}`}
      >
        放大播放
      </button>
      {expanded && (
        <Dialog title={label} close={() => setExpanded(false)}>
          <div className="dialog-body media-lightbox">
            <video
              controls
              playsInline
              preload="metadata"
              src={asset.url}
              poster={poster?.url}
              aria-label={`${label}大图播放`}
            />
          </div>
          <div className="dialog-actions">
            <button className="button primary" onClick={() => setExpanded(false)}>
              关闭预览
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}

export function MediaGallery({
  items,
  editable,
  busy,
  inspect,
  edit,
  regenerateVoice,
  fps = 24,
}: {
  items: MediaPreview[];
  editable: boolean;
  busy: boolean;
  inspect: (stepId: string) => void;
  edit: (item: MediaPreview) => void;
  regenerateVoice?: ((item: MediaPreview, lineIndex?: number) => void) | undefined;
  fps?: number;
}) {
  const visuals = items.filter((item) => item.kind !== 'voice');
  return (
    <section className="novel-candidate media-gallery">
      <div className="section-heading">
        <h2>画面与声音预览</h2>
        <Badge>
          已生成 {visuals.filter((item) => item.asset?.url).length} / {visuals.length} 镜
        </Badge>
      </div>
      <p className="quiet">
        视频可试听原声，独立配音可逐句试听和重做。失败、暂停或离开页面后，已生成结果仍会保留；整组试听确认后再批准素材。
      </p>
      <p className="quiet">
        自动生成视频时，已批准首帧承载人物造型、场景、道具和开场站位；文字重点保留运镜、后续动作、逐句对白与声音。较长的首帧修改稿由对应的已批准图片呈现，避免重复静态描述挤占视频提示词。
      </p>
      <div className="media-gallery-grid">
        {items.map((item) => (
          <article className="media-preview-card" key={item.stepId}>
            <div className="section-heading">
              <h3>
                第 {item.ordinal + 1} 镜{item.kind === 'voice' ? ' · 配音' : ''}
              </h3>
              <Badge>
                {item.asset?.url
                  ? '已生成'
                  : item.state === 'failed'
                    ? '生成失败'
                    : item.state === 'working'
                      ? '生成中'
                      : '等待生成'}
              </Badge>
            </div>
            {item.asset?.url ? (
              <MediaPlayer
                asset={item.asset}
                poster={item.poster}
                label={`第 ${item.ordinal + 1} 镜${item.kind === 'voice' ? '配音' : '画面'}`}
              />
            ) : (
              <div className="keyframe-placeholder">
                {item.state === 'failed'
                  ? '本次未返回可用素材'
                  : item.state === 'working'
                    ? '生成完成后自动显示'
                    : '尚未开始生成'}
              </div>
            )}
            {item.errorMessage && <p className="keyframe-preview-error">{item.errorMessage}</p>}
            {item.warnings?.map((warning) => (
              <p className="form-error" key={warning}>
                {warning}
              </p>
            ))}
            {item.speechPartial && (
              <p className="form-error">
                部分台词尚未完成，已成功的声音已保存。继续生成会复用仍有效的声音；台词或声音设定改变时，将生成新候选。
              </p>
            )}
            {item.speechLines && (
              <SpeechLines
                lines={item.speechLines}
                fps={fps}
                busy={busy || !editable || !['ready', 'failed', 'pending'].includes(item.state)}
                regenerate={
                  regenerateVoice && !item.speechPartial
                    ? (index) => regenerateVoice(item, index)
                    : undefined
                }
              />
            )}
            {item.kind === 'voice' && (
              <div className="media-shot-actions">
                <button
                  className="button secondary"
                  disabled={busy}
                  onClick={() => inspect(item.stepId)}
                >
                  查看配音记录
                </button>
                {regenerateVoice && (
                  <button
                    className="button secondary"
                    disabled={
                      busy || !editable || !['ready', 'failed', 'pending'].includes(item.state)
                    }
                    onClick={() => regenerateVoice(item)}
                  >
                    {item.speechPartial
                      ? '继续生成缺失台词'
                      : item.state === 'failed'
                        ? '重新尝试本镜配音'
                        : '重新生成本镜配音'}
                  </button>
                )}
                <small>要修改台词，请到制作页保存后同步；重做配音不会改写台词。</small>
              </div>
            )}
            {item.kind !== 'voice' && (
              <div className="media-shot-actions">
                <button
                  className="button secondary"
                  disabled={busy}
                  onClick={() => inspect(item.stepId)}
                >
                  查看提示词与记录
                </button>
                <button
                  className="button secondary"
                  disabled={
                    busy || !editable || !['ready', 'failed', 'pending'].includes(item.state)
                  }
                  onClick={() => edit(item)}
                >
                  {item.state === 'failed' ? '修改提示词并重试' : '编辑提示词 / 重新生成'}
                </button>
              </div>
            )}
          </article>
        ))}
      </div>
      {!editable && <p className="quiet">本轮完成或失败后，可单独重做镜头。重做后仍需审阅批准。</p>}
    </section>
  );
}

export function VisualPromptEditor({
  number,
  original,
  busy,
  paused,
  error,
  video = true,
  close,
  submit,
}: {
  number: number;
  original: string;
  busy: boolean;
  paused: boolean;
  error: string;
  video?: boolean;
  close: () => void;
  submit: (prompt: string) => Promise<void>;
}) {
  const [prompt, setPrompt] = useState(original);
  const bytes = new TextEncoder().encode(prompt).length;
  const tooLong = video && bytes > 4096;
  return (
    <Dialog title={`修改第 ${number} 镜画面提示词`} close={close}>
      <div className="dialog-body keyframe-prompt-editor">
        <p>
          修改后的全文会替换本镜头的生成提示词。系统仍会携带已批准的首帧、角色参考图及声音设定，其他已生成镜头保留。
        </p>
        <Field
          label="画面提示词"
          hint="可以调整动作、机位和表达方式；请保留需要模型生成的对白与声音说明。"
        >
          <textarea
            aria-label={`第 ${number} 镜画面提示词`}
            rows={14}
            maxLength={12000}
            value={prompt}
            disabled={busy}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </Field>
        {video && (
          <p className={tooLong ? 'form-error' : 'quiet'} role={tooLong ? 'alert' : undefined}>
            正文 {bytes} 字节 / 接口总限额 4096
            字节。提交时还会附上未包含的角色声音及参考图说明，以服务器最终校验为准。中文通常每字占 3
            字节。
          </p>
        )}
        <p className="quiet">
          这里只改变此次画面生成，不自动改写剧本或关键帧。若需要改变首帧中的人物、服装或构图，请先重做关键帧。
        </p>
        <p className="quiet">
          提交会创建新的生成请求，旧记录保留；完成后仍需你审批。尚未生成的镜头会随后继续。
        </p>
        {paused && <p className="info-box">流程已暂停，请先关闭此窗口并继续流程，再提交修改。</p>}
        {error && (
          <p className="error-banner" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="dialog-actions">
        <button className="button secondary" disabled={busy} onClick={close}>
          取消
        </button>
        <button
          className="button primary"
          disabled={busy || paused || !prompt.trim() || tooLong}
          onClick={() => void submit(prompt.trim())}
        >
          {busy ? '正在提交…' : '用此提示词重新生成本镜头'}
        </button>
      </div>
    </Dialog>
  );
}
